import produce from 'immer';
import { v4 as uuidv4 } from 'uuid';

import { BranchData } from 'api/branch/branchTypes';
import { OnboardingDto } from 'api/organization/onboardingTypes';
import { container } from 'ioc-container';
import Service from 'ioc-container/service-keys';
import { ILocalStorageService, LocalStorageKey } from 'services/LocalStorageService';

export enum Step {
  Setup = 'setup',
  Summary = 'summary',
}

export interface OnboardingContextState {
  secret: string;
  flowId: string | null;
  onboarding: undefined | null | OnboardingDto;
  step: undefined | null | Step;
  branches: BranchData[];
}

export const initialState = (): OnboardingContextState => {
  const localStorage = container.resolve(Service.localStorage) as ILocalStorageService;
  const onboardingSecret = localStorage.getItem(LocalStorageKey.OnboardingSecret) ?? uuidv4();
  localStorage.setItem(LocalStorageKey.OnboardingSecret, onboardingSecret);
  return {
    secret: onboardingSecret,
    flowId: new URLSearchParams(window.location.search).get('flowId'),
    onboarding: undefined,
    step: undefined,
    branches: [],
  };
};

export const SET_ONBOARDING = 'SET_ONBOARDING';
export const setOnboarding = (onboarding: OnboardingDto | null) => ({
  type: SET_ONBOARDING,
  payload: { onboarding },
});

export const SET_STEP = 'SET_STEP';
export const setStep = (step: undefined | null | Step) => ({
  type: SET_STEP,
  payload: { step },
});

export const SET_BRANCHES = 'SET_BRANCHES';
export const setBranches = (branches: BranchData[]) => ({
  type: SET_BRANCHES,
  payload: { branches },
});

function onboardingReducer(state: OnboardingContextState, action: any) {
  return produce(state, (draftState) => {
    switch (action.type) {
      case SET_ONBOARDING: {
        const { onboarding } = (action as ReturnType<typeof setOnboarding>).payload;
        if (
          onboarding === null ||
          draftState.onboarding == null ||
          onboarding.updatedAt > draftState.onboarding.updatedAt
        ) {
          // eslint-disable-next-line no-param-reassign
          draftState.onboarding = onboarding;
          break;
        }
        const workflows = onboarding.branchRegistrationWorkflows;
        const draftStateWorkflows = draftState.onboarding.branchRegistrationWorkflows;
        if (
          workflows.length !== draftStateWorkflows.length ||
          workflows.some((workflow, index) => workflow.id !== draftStateWorkflows[index].id)
        ) {
          // eslint-disable-next-line no-param-reassign
          draftState.onboarding.branchRegistrationWorkflows = workflows;
        } else {
          workflows.forEach((workflow, index) => {
            const draftStateWorkflow = draftStateWorkflows[index];
            if (workflow.updatedAt > draftStateWorkflow.updatedAt) {
              draftStateWorkflows[index] = workflow;
            }
          });
        }
        const branchesTaken = onboarding.branchesBelongingToOtherOrganizations;
        const draftStateBranchesTaken = draftState.onboarding.branchesBelongingToOtherOrganizations;
        if (
          branchesTaken.length !== draftStateBranchesTaken.length ||
          branchesTaken.some((branch, index) => branch !== draftStateBranchesTaken[index])
        ) {
          // eslint-disable-next-line no-param-reassign
          draftState.onboarding.branchesBelongingToOtherOrganizations = branchesTaken;
        }
        break;
      }
      case SET_STEP: {
        const { step } = (action as ReturnType<typeof setStep>).payload;
        // eslint-disable-next-line no-param-reassign
        draftState.step = step;
        break;
      }
      case SET_BRANCHES: {
        const { branches } = (action as ReturnType<typeof setBranches>).payload;
        // eslint-disable-next-line no-param-reassign
        draftState.branches = branches;
        break;
      }
      default: {
        throw new Error(`Unhandled action type: ${action.type}`);
      }
    }
  });
}

export default onboardingReducer;
